import { cronOptions } from '@/constants'
import type { OptionType } from '@/types'

import {
  formatImportFileExtensions,
  IMPORT_FILE_EXTENSIONS,
  IMPORT_FILE_FORMAT_LABELS,
} from '../../lib/stockImportsFileFormats'
import { BACKEND_MAX_FILE_SIZE_MB } from '../../lib/stockImportsFormat'
import type {
  ImportFileFormat,
  ImportSourceType,
} from '../../types/stockImports.types'
import type { ConfigSourceType } from './configFormModel'
import { normalizeCronExpression } from './configFormModel'

export type ConfigStep = 'source' | 'settings' | 'prices' | 'review'

type ConfigStepOption = { value: ConfigStep; label: string }

const CONFIG_STEPS: ConfigStepOption[] = [
  { value: 'source', label: 'Источник' },
  { value: 'settings', label: 'Настройки' },
  { value: 'review', label: 'Проверка' },
]

const CONFIG_STEPS_WITH_PRICES: ConfigStepOption[] = [
  { value: 'source', label: 'Источник' },
  { value: 'settings', label: 'Настройки' },
  { value: 'prices', label: 'Цены' },
  { value: 'review', label: 'Проверка' },
]

export const getConfigSteps = (withPrices: boolean) =>
  withPrices ? CONFIG_STEPS_WITH_PRICES : CONFIG_STEPS

export const FILE_FORMAT_OPTIONS: OptionType[] = (
  Object.keys(IMPORT_FILE_EXTENSIONS) as ImportFileFormat[]
).map((format) => ({
  value: format,
  label: `${IMPORT_FILE_FORMAT_LABELS[format]} (${formatImportFileExtensions(format)})`,
}))

export const SHEET_SELECTION_OPTIONS: OptionType[] = [
  { value: 'ALL', label: 'Все листы' },
  { value: 'BY_NAME', label: 'По именам' },
  { value: 'BY_INDEX', label: 'По номерам' },
]

export const buildSourceTypeOptions = (
  currentSourceType?: ImportSourceType
): OptionType[] => [
  { value: 'EMAIL', label: 'Системная почта' },
  { value: 'CUSTOM_IMAP', label: 'Личная почта поставщика' },
  { value: 'URL', label: 'Облачное хранилище / Прямая ссылка' },
  ...(currentSourceType === 'MANUAL'
    ? [{ value: 'MANUAL', label: 'Ручная загрузка' }]
    : []),
]

export const buildCronOptions = (cronExpression: string): OptionType[] => {
  const normalized = normalizeCronExpression(cronExpression)
  const options: OptionType[] = [...cronOptions]
  if (normalized && !options.some((option) => option.value === normalized))
    return [{ value: normalized, label: normalized }, ...options]
  return options
}

export const getValidateSourceLabel = (sourceType: ConfigSourceType) =>
  sourceType === 'URL'
    ? 'Проверить ссылку'
    : sourceType === 'EMAIL'
      ? 'Проверить настройки'
      : 'Проверить подключение'

export const COLUMN_INDEX_HINT =
  'Откройте файл и посмотрите букву нужной колонки. Нумерация начинается с единицы: A = 1, B = 2, C = 3, D = 4.'

export const CONFIG_TOOLTIPS = {
  sourceType: (
    <span>
      Системная почта — файл присылают на общий почтовый ящик платформы, склад
      определяется по связке отправитель + тема письма + маска файла.
      <br />
      Личная почта поставщика — файл забирается напрямую с личного IMAP-ящика
      поставщика.
      <br />
      Облачное хранилище / Прямая ссылка — файл загружается по публичной ссылке:
      Google Drive, Яндекс.Диск, ссылка из просмотрщика Office Online или прямая
      ссылка на файл на сайте поставщика.
    </span>
  ),
  fileFormat: (
    <span>
      Формат файла с остатками. Excel — {formatImportFileExtensions('EXCEL')};
      CSV — {formatImportFileExtensions('CSV')}; XML —{' '}
      {formatImportFileExtensions('XML')}. Форматы .xlsb, .ods и .txt не
      поддерживаются.
    </span>
  ),
  store:
    'Склад, на который будут импортироваться остатки, полученные из этого источника.',
  url: `Ссылка на файл в Google Drive, Яндекс.Диске, просмотрщике Office Online или прямая ссылка на файл (например, https://site.com/price.xlsx). Файл должен быть доступен всем, у кого есть ссылка, не быть папкой и весить не больше ${BACKEND_MAX_FILE_SIZE_MB} МБ.`,
  expectedSender:
    'Точный email из поля From. Можно указать адрес поставщика или менеджера, который пересылает прайс. Пример: manager@pips.by.',
  subjectMask:
    'Слово или фраза, которые должны встретиться в теме письма. Регистр не важен, а приписки вроде «Fwd:», «FW:» и «Re:» игнорируются. Например, для темы «Fwd: Остатки Вектор за март» укажите: Остатки Вектор.',
  fileMask:
    'Маска вложения, которое нужно взять из письма. Символ * означает любые символы. Примеры: price_*.xlsx, *остатки*, *.csv, *.tsv.',
  expirationHours:
    'Через сколько часов без новых остатков считать их устаревшими. После этого остатки склада удаляются из поиска.',
  brandColumn:
    'Можно не указывать, если выбран бренд по умолчанию или включён поиск бренда в наименовании товара.',
  allowedBrands:
    'Белый список. Если выбрать бренды, загрузятся только строки этих брендов. Все остальные строки будут отброшены.',
  ignoredBrands:
    'Черный список. Строки с этими брендами не будут загружены и попадут в % не найденной номенклатуры с причиной отсева.',
  fallbackBrand:
    'Используется, если в файле нет колонки бренда или в строке бренд пустой и не найден в наименовании. Без колонки бренда и без поиска в наименовании это поле обязательно.',
  maxAllowedMissing:
    'Критический лимит не найденной номенклатуры. Если процент нераспознанных строк выше этого значения, загрузка остановится с ошибкой, а остатки не обновятся.',
  missingSpike:
    'Допустимый скачок не найденной номенклатуры по сравнению с предыдущей успешной загрузкой. Если скачок больше, система отправит уведомление, но загрузку не остановит.',
  minLoaded:
    'Если распознано меньше этой доли от обычного количества (медиана 5 последних успешных загрузок), загрузка остановится с ошибкой, а остатки останутся прежними.',
  maxInvalidQuantity:
    'Если у большей доли строк количество пустое или не распознано, загрузка остановится с ошибкой, а остатки останутся прежними.',
  stalledDays:
    'Через сколько рабочих дней без нового письма прислать уведомление.',
  expectedQuantityHeader:
    'Защита от сдвига колонок в файле поставщика. Если заголовок над колонкой количества не совпадёт, остатки загрузятся, но придёт предупреждение. Нужна начальная строка не меньше 2.',
  textQuantityMapping:
    'Переводит текст из ячейки остатка в число перед загрузкой. Примеры: «В наличии» → 10, «Мало» → 2, «Нет» → 0.',
}

export const SOURCE_NOTICES: Partial<Record<ConfigSourceType, string>> = {
  EMAIL:
    'Письма должны приходить на централизованный ящик платформы. Система определяет нужный склад по связке отправитель + тема письма + маска файла.',
  URL: 'По публичной ссылке можно загрузить файл Excel (.xlsx, .xlsm, .xls), CSV (.csv, .tsv) или XML. Файл должен быть доступен всем, у кого есть ссылка.',
}
